// AppRoutes.tsx
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import LoginPage from "./pages/LoginPage";
import RegisterScreen from "./pages/RegisterScreen";
import UserDashboard from "./pages/UserDashboard";
import AdminMain from "./pages/AdminMain";
import ManageData from "./pages/admin/1-1.ManageData";
import Statistics from "./pages/admin/1-2.Statistics";
import ManageAccounts from "./pages/admin/1-3.ManageAccounts";
import AICuration from "./pages/admin/1-4.AICuration";
import WarehouseInventory from "./pages/admin/1-5.WarehouseInventory";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { Spinner } from "./components/ui/spinner";

const AppRoutes: React.FC = () => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        setIsAuthenticated(true);
        setFirebaseUser(user);

        if (location.pathname.startsWith("/register")) {
          setUserRole(null);
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setUserRole(data.role);
        } else {
          setUserRole(null);
        }
      } else {
        setIsAuthenticated(false);
        setFirebaseUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [location.pathname]);

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <Spinner size="lg" />
        <span style={{ color: "#666" }}>로딩 중...</span>
      </div>
    );

  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginPage setIsAuthenticated={setIsAuthenticated} />}
      />
      <Route path="/register" element={<RegisterScreen />} />

      <Route
        path="/admin/main"
        element={
          isAuthenticated && userRole === "admin" ? (
            <AdminMain />
          ) : (
            <Navigate to="/login" />
          )
        }
      >
        <Route index element={<Navigate to="/admin/main/manage-data" />} />
        <Route path="/admin/main/manage-data" element={<ManageData />} />
        <Route path="/admin/main/statistics-main" element={<Statistics />} />
        <Route
          path="/admin/main/manage-accounts"
          element={<ManageAccounts />}
        />
        <Route path="/admin/main/ai-curation" element={<AICuration />} />
        <Route
          path="/admin/main/warehouse-inventory"
          element={<WarehouseInventory />}
        />
      </Route>

      <Route
        path="/user/main"
        element={
          isAuthenticated && userRole === "user" ? (
            <UserDashboard />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      <Route
        path="*"
        element={
          isAuthenticated ? (
            userRole === "admin" ? (
              <Navigate to="/admin/main" />
            ) : userRole === "user" ? (
              <Navigate to="/user/main" />
            ) : (
              <Navigate to="/login" />
            )
          ) : (
            <Navigate to="/login" />
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
